import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { cardSurfaceSx } from '../layout/surface';

export interface FormSectionProps {
  readonly title: string;
  readonly description?: string;
  /** Grid columns for the fields. Forms read better narrow; two is the usual maximum. */
  readonly columns?: 1 | 2;
  readonly children: ReactNode;
  readonly actions?: ReactNode;
  /** Renders without the surrounding Paper — for sections already inside a dialog or wizard. */
  readonly bare?: boolean;
}

/**
 * Groups related inputs under one heading. Consistent labelling and spacing here is what keeps a
 * five-step wizard from looking like five different products.
 */
export function FormSection({
  title,
  description,
  columns = 2,
  children,
  actions,
  bare = false,
}: FormSectionProps): ReactNode {
  const body = (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h3" component="h2">
          {title}
        </Typography>
        {description !== undefined && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: columns === 1 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          },
          columnGap: 2,
          rowGap: 2,
        }}
      >
        {children}
      </Box>
      {actions !== undefined && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 2.5, flexWrap: 'wrap' }}>
          {actions}
        </Stack>
      )}
    </>
  );

  return bare ? (
    <Box component="section">{body}</Box>
  ) : (
    <Paper
      variant="outlined"
      component="section"
      sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}
    >
      {body}
    </Paper>
  );
}
